import AddIcon from "@mui/icons-material/Add";
import { Box, Button } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import FilterSelect from "../../../../../../../components/FilterSelect";
import Flex from "../../../../../../../components/Flex";
import LabeledSwitch from "../../../../../../../components/LabeledSwitch";
import CustomTabs from "../../../../../../../components/app/CustomTabs";
import EducationMaterialFilter from "../../../../../../../components/app/provider/lib/education-material/EducationMaterialFilter";
import EducationMaterialTable from "../../../../../../../components/app/provider/lib/education-material/EducationMaterialTable";
import UpsertEducationDrawer from "../../../../../../../components/app/provider/lib/education-material/UpsertEducationDrawer";
import { ChartActionStrip } from "../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { GetAllDocumentsDocumentTagEnum } from "../../../../../../../services/api";
import { roleFilter } from "../../../../../../../utils/utils";
import PatientDocumentnew from "../document/PatientDocumentnew";
import UpsertUploadDocumentDrawer from "../document/UpsertUploadDocumentDrawer";
import { DOCUMENT_TAGS } from "../document/document-constant";
import AssignEducationMaterial from "./AssignEducationMaterial";
import {
  DEFAULT_EDUCATION_MATERIAL_FILTER_VALUES,
  EducationMaterialFilterInitialValuesType,
} from "../../../../../../../components/app/provider/lib/education-material/EducationMaterialFilter";
import SearchInput from "../../../../../../../components/SearchInput";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import {
  FilterFunnelIcon,
  UploadIcon,
} from "../../../../../../../components/svg-icons/material-icons";

const PatientEducationMaterial = () => {
  const [filter, setFilter] =
    useState<EducationMaterialFilterInitialValuesType>(
      DEFAULT_EDUCATION_MATERIAL_FILTER_VALUES
    );
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [search, setSearch, instantSearchVal] = useDebounce("");
  const [archive, setArchive] = useState<boolean>(false);
  const currentActiveTab = location.pathname.split("/").pop();
  const [activeTab, setActiveTab] = useState<string>(
    currentActiveTab === "documents" || isVisitNotePage
      ? "documents"
      : currentActiveTab || ""
  );
  const [tag, setTag] = useState<GetAllDocumentsDocumentTagEnum>();
  const { hasNone: hasEducationNone } = useRoleController("Education Material");
  const { hasNone: hasDocumentNone } = useRoleController("Documents");

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ChartActionStrip
        startContent={
          <>
            <CustomTabs
              tabsData={roleFilter({
                list: TABS_DATA,
                compWith: {
                  documents: !hasDocumentNone,
                  education: !hasEducationNone,
                },
              })}
              value={activeTab}
              onChange={(v) => {
                setSearch("");
                setActiveTab(v);
                setFilter(DEFAULT_EDUCATION_MATERIAL_FILTER_VALUES);
              }}
            />
            {activeTab === "education" && !isVisitNotePage && (
              <LabeledSwitch
                label="Show Archived Materials"
                checked={archive}
                onChange={(e) => setArchive(e.target.checked)}
              />
            )}
          </>
        }
        endContent={
          !isVisitNotePage ? (
            <>
              {activeTab === "education" && (
                <EndActions
                  onApply={setFilter}
                  filters={filter}
                  instantSearchVal={instantSearchVal}
                  setSearch={setSearch}
                  activeTab={activeTab}
                  setFilter={setFilter}
                />
              )}
              {activeTab === "documents" && (
                <EndActionSectionDocuments
                  setTag={setTag}
                  tag={tag}
                  instantSearchVal={instantSearchVal}
                  setSearch={setSearch}
                />
              )}
            </>
          ) : undefined
        }
      />

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {activeTab === "education" && (
          <EducationMaterialTable
            isPatientChartView
            filter={filter}
            search={search}
            archive={archive}
            setFilter={setFilter}
          />
        )}
        {activeTab === "documents" && (
          <PatientDocumentnew
            tag={tag as GetAllDocumentsDocumentTagEnum}
            search={search}
          />
        )}
      </Box>
    </Box>
  );
};

export default PatientEducationMaterial;

const EndActions = ({
  onApply,
  filters,
  instantSearchVal,
  setSearch,
  activeTab,
  setFilter,
}: {
  onApply: (v: any) => void;
  filters: any;
  instantSearchVal: string;
  setSearch: (v: string) => void;
  activeTab: string;
  setFilter: (v: EducationMaterialFilterInitialValuesType) => void;
}) => {
  const [uploadDisclosureHost, assignDisclosureHost] = useReduceDisclosure(2);
  const { patientId } = useParams();
  const { hasUpdate: hasUpdateEducationMaterial } =
    useRoleController("Education Material");

  return (
    <Flex gap={2} alignItems="center" justifyContent="center">
      {activeTab === "education" && (
        <Flex gap={2} alignItems="center" justifyContent="center">
          <Box width="280px">
            <SearchInput
              searchKey={instantSearchVal}
              onSearch={(val) => setSearch(val)}
              placeholder="Search by Title, Condition"
            />
          </Box>

          <EducationMaterialFilter
            onApply={onApply}
            filters={filters}
            setFilter={setFilter}
            starIcon={<FilterFunnelIcon />}
          />

          {hasUpdateEducationMaterial && (
            <StyledButtonV2
              label="Upload"
              variant="outlined"
              sIcon={<UploadIcon />}
              color="primary"
              sx={{ bgcolor: "#FFFFFF" }}
              onClick={() => uploadDisclosureHost.open()}
            />
          )}

          {hasUpdateEducationMaterial && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              color="primary"
              onClick={() => assignDisclosureHost.open({ patientId })}
            >
              Assign Edu. Material
            </Button>
          )}

          <UpsertEducationDrawer disclosureHost={uploadDisclosureHost} />
          <AssignEducationMaterial disclosureHost={assignDisclosureHost} />
        </Flex>
      )}
      {activeTab === "documents" && (
        <Button variant="contained" startIcon={<AddIcon />} color="primary">
          Upload New Document
        </Button>
      )}
    </Flex>
  );
};

interface EndActionSectionDocumentsProps {
  setTag: any;
  tag: any;
  instantSearchVal: string;
  setSearch?: any;
}

const EndActionSectionDocuments = ({
  setTag,
  tag,
  instantSearchVal,
  setSearch,
}: EndActionSectionDocumentsProps) => {
  const uploadDocDisclosure = useDisclosure();
  const { hasUpdate: hasUpdateDocuments } = useRoleController("Documents");

  return (
    <>
      <UpsertUploadDocumentDrawer disclosureHost={uploadDocDisclosure} />

      <Box width="200px">
        <SearchInput
          searchKey={instantSearchVal}
          onSearch={(val) => setSearch(val)}
          placeholder={`Search by Document Title`}
        />
      </Box>
      <Box width={"200px"}>
        <FilterSelect
          value={tag as any}
          placeholder="Select Tag"
          options={DOCUMENT_TAGS}
          onChange={(v) => setTag(v as any)}
        />
      </Box>
      {hasUpdateDocuments && (
        <ChartActionStrip.Button
          startIcon={<AddIcon />}
          onClick={() => uploadDocDisclosure.open()}
        >
          Upload Document
        </ChartActionStrip.Button>
      )}
    </>
  );
};

const TABS_DATA = [
  {
    value: "documents",
    label: "Document",
  },
  {
    value: "education",
    label: "Education",
  },
];
